import React from 'react';
import { Link } from 'react-router-dom';
import PublicNav from './PublicNav';
import { PRODUCT_DISPLAY_NAME } from '../constants/branding';
import {
  INTEGRATION_PILLS,
  PUBLIC_IMAGES,
  TRUST_STATS,
} from './publicPageAssets';
import {
  SALES_BENEFITS,
  SALES_CHALLENGES,
  SALES_CTA_POINTS,
  SALES_MODULES,
  SALES_SOLUTION_POINTS,
  SALES_WORKFLOW,
} from '../utils/salesPresentationPdf';
import '../../assets/css/public-pages.css';

export default function ProductLanding() {
  return (
    <div className="pp-page">
      <PublicNav />

      <section className="pp-hero">
        <div
          className="pp-hero-bg"
          style={{ backgroundImage: `url(${PUBLIC_IMAGES.heroBg})` }}
          aria-hidden
        />
        <div className="pp-hero-bg-overlay" aria-hidden />
        <div className="pp-container pp-hero-grid">
          <div className="pp-hero-copy">
            <span className="pp-eyebrow pp-eyebrow--light">Real estate CRM</span>
            <h1>
              Turn leads into closings with{' '}
              <span className="pp-accent" style={{ WebkitTextFillColor: '#a5b4fc', color: '#a5b4fc' }}>
                {PRODUCT_DISPLAY_NAME}
              </span>
            </h1>
            <p>
              One platform for Meta leads, follow-ups, property inventory, and team productivity —
              built for agencies, builders, and sales teams across India.
            </p>
            <div className="pp-hero-actions">
              <Link to="/contact-sales" className="pp-btn-primary">
                <i className="fas fa-paper-plane" aria-hidden /> Request a demo
              </Link>
              <Link to="/login" className="pp-btn-ghost">
                <i className="fas fa-sign-in-alt" aria-hidden /> Sign in to CRM
              </Link>
            </div>
            <div className="pp-hero-pills">
              {INTEGRATION_PILLS.map((p) => (
                <span key={p.label} className="pp-hero-pill">
                  <i className={p.icon} style={{ color: p.tone }} aria-hidden />
                  {p.label}
                </span>
              ))}
            </div>
          </div>

          <div className="pp-hero-visual">
            <div className="pp-hero-visual-glow" aria-hidden />
            <div className="pp-hero-visual-stack">
              <img
                className="pp-hero-image-accent"
                src={PUBLIC_IMAGES.heroAccent}
                alt=""
                aria-hidden
              />
              <img
                className="pp-hero-image-main"
                src={PUBLIC_IMAGES.hero}
                alt="Modern luxury property listing"
              />
              <div className="pp-hero-dashboard">
                <div className="pp-hero-dashboard-head">
                  <span>Live pipeline</span>
                  <span className="pp-hero-dashboard-live">
                    <i className="fas fa-circle" aria-hidden /> Live
                  </span>
                </div>
                <div className="pp-hero-dashboard-row">
                  <span className="pp-hero-dashboard-icon pp-hero-dashboard-icon--meta">
                    <i className="fab fa-facebook-f" aria-hidden />
                  </span>
                  <div>
                    <strong>New lead — 3BHK enquiry</strong>
                    <span>From Meta Lead Ad · 2 min ago</span>
                  </div>
                </div>
                <div className="pp-hero-dashboard-row">
                  <span className="pp-hero-dashboard-icon pp-hero-dashboard-icon--visit">
                    <i className="fas fa-calendar-check" aria-hidden />
                  </span>
                  <div>
                    <strong>Site visit scheduled</strong>
                    <span>Agent assigned · Tomorrow 11 AM</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="pp-hero-float pp-hero-float--meta">
              <span
                className="pp-hero-float-icon"
                style={{ background: 'linear-gradient(135deg,#1877f2,#0866ff)' }}
              >
                <i className="fab fa-facebook-f" aria-hidden />
              </span>
              Meta lead captured
            </div>
            <div className="pp-hero-float pp-hero-float--leads">
              <span
                className="pp-hero-float-icon"
                style={{ background: 'linear-gradient(135deg,#5e72e4,#825ee4)' }}
              >
                <i className="fas fa-user-plus" aria-hidden />
              </span>
              Assigned to agent
            </div>
            <div className="pp-hero-float pp-hero-float--deal">
              <span
                className="pp-hero-float-icon"
                style={{ background: 'linear-gradient(135deg,#2dce89,#11cdef)' }}
              >
                <i className="fas fa-handshake" aria-hidden />
              </span>
              Deal in progress
            </div>
          </div>
        </div>
      </section>

      <section className="pp-stats">
        <div className="pp-container pp-stats-grid">
          {TRUST_STATS.map((s) => (
            <div key={s.label}>
              <div className="pp-stat-value">{s.value}</div>
              <div className="pp-stat-label">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="pp-section">
        <div className="pp-container pp-split">
          <div className="pp-split-media">
            <div className="pp-image-frame">
              <img src={PUBLIC_IMAGES.team} alt="Sales team collaborating on leads" />
              <div className="pp-image-badge">
                <i className="fas fa-users" style={{ marginRight: 8 }} aria-hidden />
                Your entire team — admins, agents &amp; managers — on one workspace
              </div>
            </div>
          </div>
          <div className="pp-split-copy">
            <span className="pp-eyebrow">The challenge</span>
            <h2 className="pp-title">Spreadsheets can&apos;t run a modern agency</h2>
            <p className="pp-subtitle" style={{ marginBottom: 24 }}>
              Growing real estate teams lose revenue when leads live in too many places.
            </p>
            <ul className="pp-challenge-list">
              {SALES_CHALLENGES.map((c) => (
                <li key={c}>
                  <i className="fas fa-times-circle" style={{ color: '#f5365c', marginTop: 3 }} aria-hidden />
                  {c}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="pp-section pp-section--tight" style={{ background: '#fff' }}>
        <div className="pp-container">
          <span className="pp-eyebrow">The solution</span>
          <h2 className="pp-title pp-title--center">
            Everything you need in one CRM
          </h2>
          <p className="pp-subtitle pp-subtitle--center" style={{ marginBottom: 40 }}>
            {PRODUCT_DISPLAY_NAME} connects lead capture, assignment, follow-ups, and closing —
            from Facebook form to booked site visit.
          </p>
          <div className="pp-card-grid" style={{ marginBottom: 40 }}>
            {SALES_SOLUTION_POINTS.map((s) => (
              <div key={s.title} className="pp-card">
                <div
                  className="pp-card-icon"
                  style={{ background: 'rgba(94,114,228,0.12)', color: '#5e72e4' }}
                >
                  <i className="fas fa-check-circle" aria-hidden />
                </div>
                <h3>{s.title}</h3>
                <p>{s.text}</p>
              </div>
            ))}
          </div>
          <div className="pp-workflow">
            {SALES_WORKFLOW.map((w) => (
              <div key={w.step} className="pp-workflow-step">
                <div className="pp-workflow-num">{w.step}</div>
                <strong style={{ display: 'block', marginBottom: 6 }}>{w.title}</strong>
                <span style={{ fontSize: 13, color: '#8392ab', lineHeight: 1.45 }}>{w.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="pp-section">
        <div className="pp-container">
          <div className="pp-social-band">
            <div className="pp-social-band-copy">
              <h2>Capture leads from Meta &amp; social — automatically</h2>
              <p>
                Facebook &amp; Instagram Lead Ads flow straight into your CRM via webhooks and
                scheduled backup polling. No manual copy-paste from ads manager.
              </p>
              <div className="pp-social-icons">
                <span className="pp-social-icon-chip">
                  <i className="fab fa-facebook" aria-hidden /> Facebook Lead Ads
                </span>
                <span className="pp-social-icon-chip">
                  <i className="fab fa-instagram" aria-hidden /> Instagram forms
                </span>
                <span className="pp-social-icon-chip">
                  <i className="fab fa-whatsapp" aria-hidden /> WhatsApp-ready data
                </span>
              </div>
            </div>
            <div className="pp-social-band-img">
              <img src={PUBLIC_IMAGES.mobile} alt="Mobile CRM and social lead notifications" />
            </div>
          </div>
        </div>
      </section>

      <section className="pp-section pp-section--tight">
        <div className="pp-container pp-split pp-split--reverse">
          <div className="pp-split-media">
            <div className="pp-image-frame">
              <img src={PUBLIC_IMAGES.property} alt="Modern luxury property listing" />
              <div className="pp-image-badge">
                <i className="fas fa-building" style={{ marginRight: 8 }} aria-hidden />
                List projects, units, bookings &amp; buyer interests in one place
              </div>
            </div>
          </div>
          <div className="pp-split-copy">
            <span className="pp-eyebrow">Platform modules</span>
            <h2 className="pp-title">Built for real estate workflows</h2>
            <p className="pp-subtitle" style={{ marginBottom: 20 }}>
              From first inquiry to deal done — every module your agency needs, in one subscription.
            </p>
            <Link to="/contact-sales" className="pp-btn-primary">
              See it in action <i className="fas fa-arrow-right" aria-hidden />
            </Link>
          </div>
        </div>
      </section>

      <section className="pp-section" style={{ background: '#fff' }}>
        <div className="pp-container">
          <h2 className="pp-title pp-title--center">Platform modules</h2>
          <p className="pp-subtitle pp-subtitle--center" style={{ marginBottom: 40 }}>
            Everything your team needs to convert inquiries into closed deals
          </p>
          <div className="pp-card-grid">
            {SALES_MODULES.map((m) => (
              <div key={m.title} className="pp-card">
                <div
                  className="pp-card-icon"
                  style={{ background: `${m.tone}18`, color: m.tone }}
                >
                  <i className={m.icon} aria-hidden />
                </div>
                <h3>{m.title}</h3>
                <p>{m.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="pp-section pp-section--tight">
        <div className="pp-container">
          <h2 className="pp-title pp-title--center">Why agencies choose {PRODUCT_DISPLAY_NAME}</h2>
          <p className="pp-subtitle pp-subtitle--center" style={{ marginBottom: 36 }}>
            Measurable impact on sales discipline and conversion
          </p>
          <div className="pp-benefit-grid">
            {SALES_BENEFITS.map((b) => (
              <div key={b.label} className="pp-benefit">
                <div className="pp-benefit-stat">{b.stat}</div>
                <strong>{b.label}</strong>
                <p>{b.detail}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="pp-section">
        <div className="pp-container">
          <div className="pp-cta">
            <div
              className="pp-cta-bg"
              style={{ backgroundImage: `url(${PUBLIC_IMAGES.skyline})` }}
            />
            <div className="pp-cta-overlay" />
            <div className="pp-cta-content">
              <h2>Ready to modernize your sales operations?</h2>
              <ul className="pp-cta-list">
                {SALES_CTA_POINTS.map((p) => (
                  <li key={p}>
                    <i className="fas fa-check" aria-hidden />
                    {p}
                  </li>
                ))}
              </ul>
              <Link to="/contact-sales" className="pp-btn-white">
                <i className="fas fa-calendar-check" aria-hidden /> Book a demo
              </Link>
            </div>
          </div>
        </div>
      </section>

      <footer className="pp-footer">
        © {new Date().getFullYear()} {PRODUCT_DISPLAY_NAME} — All rights reserved.
      </footer>
    </div>
  );
}
